
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { AppWindow, Smartphone, Globe, Cloud, Code, Puzzle, RefreshCw, Download, ChevronRight, Zap, Shield, Layers } from 'lucide-react';
import '../styles/SharedStyles.css';

const appFeatures = [
  {
    icon: Smartphone,
    title: 'Mobile Applications',
    description: 'Native iOS and Android apps designed for seamless user experience and optimal performance.',
    color: '#6366f1'
  },
  {
    icon: Globe,
    title: 'Web Applications',
    description: 'Progressive web apps with responsive design, offline support, and cross-browser compatibility.',
    color: '#0ea5e9'
  },
  {
    icon: Cloud,
    title: 'Cloud Solutions',
    description: 'Scalable cloud infrastructure with auto-deployment, monitoring, and disaster recovery.',
    color: '#10b981'
  },
  {
    icon: Code,
    title: 'Custom Development',
    description: 'Tailored software solutions built with modern frameworks and best practices.',
    color: '#f59e0b'
  },
  {
    icon: Puzzle,
    title: 'Third-Party Integrations',
    description: 'Seamless API integrations with popular services, payment gateways, and platforms.',
    color: '#ec4899'
  },
  {
    icon: RefreshCw,
    title: 'Auto Updates',
    description: 'Continuous delivery pipeline ensuring your software stays current with zero downtime.',
    color: '#8b5cf6'
  },
  {
    icon: Download,
    title: 'Software Distribution',
    description: 'Secure app stores, licensing management, and automated deployment workflows.',
    color: '#ef4444'
  },
  {
    icon: Layers,
    title: 'Microservices',
    description: 'Modular architecture enabling independent scaling, development, and deployment.',
    color: '#14b8a6'
  },
  {
    icon: Shield,
    title: 'Security & Compliance',
    description: 'Enterprise-grade security with GDPR, SOC2, and industry-specific compliance.',
    color: '#f97316'
  }
];

const stats = [
  { value: '150+', label: 'Apps Deployed' },
  { value: '99.8%', label: 'Crash-Free Rate' },
  { value: '50M+', label: 'Downloads' },
  { value: '24/7', label: 'Support' }
];

const AppSoftware = () => {
  const { t } = useLanguage();
  const { isActive, isAdmin } = useAuth();
  const { theme } = useTheme();

  return (
    <div className={`shared-page ${theme}`}>
      <div className="glow-effect glow-1"></div>
      <div className="glow-effect glow-2"></div>
      <div className="container shared-content" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>

        <div className="shared-section-header">
          <h2>
            <AppWindow size={28} />
            App & Software
          </h2>
          <p>Comprehensive application development and software management solutions</p>
        </div>

        {/* Stats Section */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '1.5rem',
          marginBottom: '3rem',
          animation: 'slideUp 0.8s ease-out forwards',
          opacity: 0
        }}>
          {stats.map((stat, index) => (
            <div key={index} className="shared-feature-card" style={{
              '--card-color': '#6366f1',
              textAlign: 'center',
              minHeight: 'auto',
              padding: '1.5rem',
              animationDelay: `${index * 0.1}s`
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 800, background: 'linear-gradient(135deg, #6366f1 0%, #0ea5e9 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {stat.value}
              </div>
              <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.25rem' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="shared-grid">
          {appFeatures.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className="shared-feature-card" style={{ '--card-color': feature.color, animationDelay: `${0.1 + index * 0.1}s` }}>
                <div className="shared-card-icon" style={{ backgroundColor: feature.color }}>
                  <Icon size={28} />
                </div>
                <h3>{feature.title}</h3>
                <p className="shared-card-desc">{feature.description}</p>
                <a href="#" className="shared-card-link" onClick={(e) => e.preventDefault()}>
                  Learn More
                  <ChevronRight size={16} />
                </a>
                <div className="shared-card-arrow">
                  <ChevronRight size={20} />
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default AppSoftware;
